import { Button, CircularProgress } from '@shared/components';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useExecuteTool } from '@shared/graphql/hooks/tools/useExecuteTool';
import {
  ActionExecutionEventData,
  SupervisorToolUsedEventData,
  ToolUsedEventData,
} from '@shared/types/action-execution';
import { PiPlayFill } from 'react-icons/pi';

type ExecuteToolButtonProps = {
  data: ToolUsedEventData | SupervisorToolUsedEventData;
  onEditEventData: (updatedData: ActionExecutionEventData) => void;
};

export function ExecuteToolButton({
  data,
  onEditEventData,
}: ExecuteToolButtonProps) {
  const { defaultOrgId } = useMeContext();
  const { sendToast } = useToast();
  const { executeTool, loading: isExecuting } = useExecuteTool();

  const handleRunTool = async () => {
    if (!defaultOrgId) return;

    try {
      const result = await executeTool(
        data.toolName,
        data.toolInput,
        defaultOrgId
      );
      onEditEventData({
        ...data,
        toolOutput: JSON.parse(result.toolOutput),
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to execute tool';
      sendToast(errorMessage, { variant: 'error' });
    }
  };

  if (!defaultOrgId) return null;

  return (
    <Button
      size="xs"
      isGhost
      onPress={handleRunTool}
      isDisabled={isExecuting}
      icon={
        isExecuting ? (
          <CircularProgress size="xs" />
        ) : (
          <PiPlayFill size={12} className="text-gray-500" />
        )
      }
      tooltip="Run tool with current inputs"
      labelClassName="text-gray-500"
    >
      Run Tool
    </Button>
  );
}
